import stageKey from "Utils/stageKey";

const DAY = 86400000;
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

// rows: page-local task table rows ({ task, contact }).
// actions is optional: { showOverdue, startWith(taskId), filterType(type) }.
const taskFindings = (rows = [], { now = Date.now(), actions = {} } = {}) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return [];
  const out = [];

  const overdue = list.filter(r => r.task?.taskDue && r.task.taskDue < now);
  if (overdue.length) {
    const oldest = Math.floor((now - Math.min(...overdue.map(r => r.task.taskDue))) / DAY);
    out.push({
      tone: "warn",
      key: "overdue",
      label: plural(overdue.length, "task") + " overdue",
      text: `Oldest is ${plural(oldest, "day")} late. Clear these before today's list.`,
      actions: actions.showOverdue ? [{ label: "Show overdue", run: actions.showOverdue }] : []
    });
  }

  const bounced = list.filter(r => stageKey(r.contact?.stage) === "BOUNCED");
  if (bounced.length) {
    out.push({
      tone: "bad",
      key: "bounced",
      label: plural(bounced.length, "task") + " for a bounced contact",
      text: "Their email bounced, so the task will not land. Skip it or enrich the contact first.",
      actions: actions.askCopilot
        ? [{ label: "Skip them", run: () => actions.askCopilot("Skip the tasks whose contact bounced or opted out. Show me the list and ask me to confirm.") }]
        : []
    });
  }

  const warm = list
    .filter(r => r.contact?.replied || r.contact?.stage === "REPLIED" || r.contact?.clickCount > 0 || r.contact?.openCount > 0)
    .sort((a, b) => (b.contact?.replied || b.contact?.stage === "REPLIED" ? 1 : 0) - (a.contact?.replied || a.contact?.stage === "REPLIED" ? 1 : 0));
  if (warm.length) {
    out.push({
      tone: "hot",
      key: "warm",
      label: plural(warm.length, "contact") + " already engaged",
      text: "Replied, clicked or opened. Warm hands first.",
      actions: actions.startWith
        ? [{ label: "Start with them", run: () => actions.startWith(warm[0].task?.id) }]
        : []
    });
  }

  const byType = {};
  list.forEach(r => {
    const k = r.task?.taskType || "other";
    byType[k] = (byType[k] || 0) + 1;
  });
  const [topType, topCount] = Object.entries(byType).sort((a, b) => b[1] - a[1])[0] || [];
  if (topType && topCount / list.length > 0.6 && list.length >= 4) {
    out.push({
      tone: "neutral",
      key: "dominant_type",
      label: `Mostly ${topType} tasks`,
      text: `${topCount} of ${list.length} are ${topType}. Batch them in one sitting.`,
      actions: actions.filterType ? [{ label: "Only these", run: () => actions.filterType(topType) }] : []
    });
  }

  const optedOut = list.filter(r => ["OPTED_OUT", "DO_NOT_CONTACT"].includes(stageKey(r.contact?.stage)));
  if (optedOut.length) {
    out.push({
      tone: "bad",
      key: "opted_out",
      label: plural(optedOut.length, "task") + " for an opted-out contact",
      text: "They asked not to be contacted. Skip these.",
      actions: actions.askCopilot
        ? [{ label: "Skip them", run: () => actions.askCopilot("Skip the tasks whose contact bounced or opted out. Show me the list and ask me to confirm.") }]
        : []
    });
  }

  const perContact = {};
  list.forEach(r => {
    const id = r.task?.contactId || r.contact?.id;
    if (id) perContact[id] = (perContact[id] || 0) + 1;
  });
  const dupes = Object.values(perContact).filter(n => n > 1).length;
  if (dupes) {
    out.push({
      tone: "neutral",
      key: "duplicate_contact",
      label: plural(dupes, "contact") + " with more than one task",
      text: "One touch per contact per day. Merge or space them out."
    });
  }

  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = dayStart.getTime() + DAY;
  const today = list.filter(r => r.task?.taskDue >= dayStart.getTime() && r.task.taskDue < dayEnd);
  if (today.length) {
    out.push({
      tone: "hot",
      key: "today_plan",
      label: plural(today.length, "task") + " due today",
      text: "Start the run and I will keep the order: replies, then clicks, then cold.",
      actions: actions.startToday ? [{ label: "Start today's run", run: actions.startToday }] : []
    });
  }

  return out;
};

export default taskFindings;
